import React, { useEffect, useState } from 'react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { CancelButton, FeedbackSnackbar, NextButton, PrevButton, Text } from '../1elements'
import ProvinceSelect from '../2compounds/ProvinceSelect'
import CountrySelect from '../2compounds/CountrySelect'
import BuildingName from '../2compounds/BuildingName'
import FormTextField from '../2compounds/FormTextField'
import DocumentsImporter from '../3modules/LocationImport/DocumentImporter'
import LastInspectionDatePicker from '../3modules/LocationImport/LastInspectionDatePicker'
import PhoneField from '../2compounds/PhoneField'

import {
  validateAddress,
  validateAddress2, validateAutocompleteBuildingName,
  validateBuildingName,
  validateCity,
  validateDescription,
  validateFMZ,
  validateJobID,
  validateLocationName,
  validatePhoneNumber,
  validatePostalCode,
  validateProvince, validateRecordName, validateRecordURL,
  validateSecurityAccess,
  validateSqFootage,
  validateTransitID,
  validateUnitNumber
} from '../../services/util/editChecks'
import { getItemFromSessionStorage, setItemToSessionStorage } from '../../services/util/sessionStorageManager'
import ButtonLayoutDiv from '../4layout/ButtonLayoutDiv'
import { formatDocumentList } from '../../services/util/formatter'
import ApiService from '../../common/ApiCalls'
import { checkAddressExistence, removeSpace } from '../../services/util/miscellaneousService'
import FormErrorSnackbar from '../2compounds/FormErrorSnackbar'
import { englishTexts as words } from '../../consts/Texts'

const LocationImportTemplate = ({ goToNextPage, goToPreviousPage, onCancel, disabled }) => {
  // Building
  const buildingDataDefault = {
    systemId: '',
    becId: '',
    selectedExistingBuildingData: null,
    name: ''
  }
  const [buildingData, setBuildingData] = useState(buildingDataDefault)
  const updateBuildingData = (key, newValue) => {
    setBuildingData(prevState => { return { ...prevState, [key]: newValue } })
  }

  // Location
  const [locationData, setLocationData] = useState({
    jobId: '',
    transitId: '',
    name: '',
    description: '',
    sqFootage: '',
    fmz: '',
    securityAccess: '',
    lastInspectionDate: null,
    documentList: [{ title: '', url: '' }]
  })
  const updateLocationData = (key, newValue) => {
    setLocationData(prevState => { return { ...prevState, [key]: newValue } })
  }

  // Address
  const addressDataDefault = {
    systemId: '',
    addressLine1: '',
    addressLine2: '',
    unit: '',
    city: '',
    province: '',
    country: 'Canada',
    postalCode: '',
    phoneNumber: ''
  }
  const [addressData, setAddressData] = useState(addressDataDefault)
  const updateAddressData = (key, newValue) => {
    setAddressData(prevState => { return { ...prevState, [key]: newValue } })
  }

  const [buildingNameWasSelected, setBuildingNameWasSelected] = useState(false)
  const [showError, setShowError] = useState(false)
  const [fieldType, setFieldType] = useState('Autocomplete')
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
  const [addressErrorSnackbar, setAddressErrorSnackbar] = useState({
    severity: 'fail',
    isOpen: false,
    message: 'We could not find the address on the map. Please input correct address'
  })
  const resetBuildingRelatedInput = () => {
    updateBuildingData('becId', '')
    setAddressData(addressDataDefault)
  }

  const onFieldTypeChange = (fieldType) => {
    resetBuildingRelatedInput()
    setBuildingData(buildingDataDefault)
    setBuildingNameWasSelected(false)
    setFieldType(fieldType)
  }

  const hasError = async () => {
    const {
      name: locationName, description, jobId, transitId, securityAccess, fmz, sqFootage, documentList
    } = locationData
    const {
      addressLine1, unit, city, province, postalCode, phoneNumber
    } = addressData
    const {
      name: buildingName, selectedExistingBuildingData
    } = buildingData

    const jobIdHasError = validateJobID(jobId)
    const buildingNameHasError = validateBuildingName(buildingName) &&
            validateAutocompleteBuildingName(selectedExistingBuildingData)

    const descriptionHasError = validateDescription(description)
    const transitIdHasError = validateTransitID(transitId)
    const locationNameHasError = validateLocationName(locationName)
    const addressLine1HasError = validateAddress(addressLine1)
    const unitHasError = validateUnitNumber(unit)
    const cityHasError = validateCity(city)
    const provinceHasError = validateProvince(province)
    const postalCodeHasError = validatePostalCode(postalCode)
    const phoneHasError = validatePhoneNumber(phoneNumber)
    const squareFootageHasError = validateSqFootage(sqFootage)
    const fmzHasError = validateFMZ(fmz)
    const securityAccessHasError = validateSecurityAccess(securityAccess)

    let documentsHasError = false
    for (let i = 0; i < documentList.length; i++) {
      if ((documentList[i].url &&
                    validateRecordName(documentList[i].title)) ||
                (documentList[i].title && validateRecordURL(documentList[i].url))) {
        documentsHasError = true
        break
      }
    }

    const baseCheck = jobIdHasError || buildingNameHasError || descriptionHasError ||
            transitIdHasError || locationNameHasError || addressLine1HasError || unitHasError || cityHasError ||
            provinceHasError || squareFootageHasError || fmzHasError || securityAccessHasError || documentsHasError || phoneHasError

    if (fieldType === 'Autocomplete') {
      return baseCheck
    }

    return baseCheck || postalCodeHasError
  }
  const saveDataToStorage = () => {
    const {
      name: locationName, description, jobId, transitId, securityAccess, fmz, sqFootage, lastInspectionDate, documentList
    } = locationData
    const {
      systemId, addressLine1, addressLine2, unit, city, province, country, postalCode, phoneNumber
    } = addressData
    const {
      selectedExistingBuildingData, name: buildingName
    } = buildingData
    const buildingSysId = selectedExistingBuildingData && selectedExistingBuildingData.building_id
      ? selectedExistingBuildingData.building_id
      : ''
    const buildName = selectedExistingBuildingData && selectedExistingBuildingData.name
      ? selectedExistingBuildingData.name
      : buildingName
    const userInput = {
      locationName: locationName,
      description: description,
      jobId: jobId,
      transitId: transitId,
      securityAccess: securityAccess,
      fmz: fmz,
      squareFootage: sqFootage,
      lastInspectionDate: lastInspectionDate || '',
      documents: formatDocumentList(documentList),
      documentList: documentList,
      buildingNameWasSelected: buildingNameWasSelected,
      fieldType: fieldType,
      address: {
        address_id: systemId,
        address1: addressLine1,
        address2: addressLine2,
        unit: unit,
        city: city,
        province: province,
        country: country,
        postalCode: postalCode,
        phoneNumber: phoneNumber
      },
      building: {
        importBuildingName: buildName,
        buildingSysId: buildingSysId,
        buildingName: selectedExistingBuildingData
      }
    }
    setItemToSessionStorage('newLocation', userInput)
  }

  const {
    name: locationName, description, jobId, transitId, securityAccess, fmz, sqFootage, lastInspectionDate, documentList
  } = locationData
  const {
    addressLine1, addressLine2, unit, city, province, country, postalCode, phoneNumber
  } = addressData
  const {
    name: buildingName, selectedExistingBuildingData
  } = buildingData

  const classes = useStyles()

  const prevHandler = () => {
    saveDataToStorage()
    goToPreviousPage()
  }

  const nextHandler = async () => {
    setShowError(true)

    if (!disabled && await hasError()) {
      setIsSnackbarOpen(true)
    } else {
      switch (fieldType) {
        case 'Autocomplete':
          saveDataToStorage()
          goToNextPage()
          break
        default:
          const isAddressValid = checkAddressExistence(addressData)
          if (isAddressValid) {
            saveDataToStorage()
            goToNextPage()
          } else {
            setAddressErrorSnackbar(prevState => { return { ...prevState, isOpen: true } })
          }
      }
    }
  }

  useEffect(() => {
    const dataFromStorage = getItemFromSessionStorage('newLocation')
    if (dataFromStorage) {
      const {
        jobId, description, transitId, locationName, squareFootage, fmz, securityAccess, lastInspectionDate,
        fieldType, documentList, buildingNameWasSelected
      } = dataFromStorage
      const { buildingSysId, buildingName, importBuildingName } = dataFromStorage.building
      const { address_id, unit, address1, address2, city, province, postalCode, phoneNumber } = dataFromStorage.address
      setLocationData(prevState => {
        return {
          ...prevState,
          jobId,
          transitId,
          name: locationName,
          description,
          sqFootage: squareFootage,
          fmz,
          securityAccess,
          lastInspectionDate,
          documentList
        }
      })

      setBuildingData(prevState => {
        return {
          ...prevState,
          systemId: buildingSysId,
          name: importBuildingName,
          selectedExistingBuildingData: buildingName
        }
      })

      setAddressData(prevState => {
        return {
          ...prevState,
          addressLine1: address1,
          addressLine2: address2,
          unit,
          city,
          province,
          postalCode,
          phoneNumber
        }
      })

      setFieldType(fieldType)
      updateAddressData('systemId', address_id)
      setBuildingNameWasSelected(buildingNameWasSelected)
    }
  }, [])

  useEffect(() => {
    // Populate address input when an existing building is selected
    const selectedExistingBuilding = buildingData.selectedExistingBuildingData
    if (selectedExistingBuilding) {
      const dataSet = { buildingId: selectedExistingBuilding.building_id }
      ApiService().getBuildingAndAddressByBuildingId(dataSet)
        .then((res) => {
          setAddressData(prevState => {
            return {
              ...prevState,
              addressLine1: res.line_1,
              addressLine2: res.line_2,
              city: res.city,
              province: res.state,
              country: res.country,
              postalCode: removeSpace(res.zip_postcode),
              phoneNumber: res.phone_number,
              systemId: res.address_id
            }
          })
          setBuildingNameWasSelected(true)
        })
    } else {
      setBuildingNameWasSelected(false)
    }
  }, [buildingData.selectedExistingBuildingData])

  return (
    <div className={classes.root}>
      <Text type='adminDashboardPageHeader'>Create new location</Text>
      <div className={classes.locationForm}>
        {/* JOB ID */}
        <FormTextField
          label='Job ID'
          hint='Max. 45 characters'
          value={jobId}
          onChange={(newValue) => updateLocationData('jobId', newValue)}
          validation={showError && validateJobID(jobId)}
          required
          disabled={disabled}
        />
        {/* BUILDING NAME */}
        <BuildingName
          manualInput={buildingName}
          setManualInput={(newValue) => updateBuildingData('name', newValue)}
          value={selectedExistingBuildingData}
          setValue={(newValue) => updateBuildingData('selectedExistingBuildingData', newValue)}
          showError={showError}
          fieldType={fieldType}
          setFieldType={onFieldTypeChange}
          province={province}
          disabled={disabled}
        />
        {/* LOCATION DESCRIPTION */}
        <FormTextField
          label='Location Description'
          hint='Max. 255 characters'
          value={description}
          onChange={(newValue) => updateLocationData('description', newValue)}
          rows={5}
          validation={showError && validateDescription(description)}
          disabled={disabled}
        />
        {/* TRANSIT ID */}
        <FormTextField
          label={words.transitId}
          hint='Max. 45 characters'
          required
          value={transitId}
          onChange={(newValue) => updateLocationData('transitId', newValue)}
          validation={showError && validateTransitID(transitId)}
          disabled={disabled}
        />
        {/* LOCATION NAME */}
        <FormTextField
          label='Location Name'
          hint='Max. 255 characters'
          required value={locationName}
          onChange={(newValue) => updateLocationData('name', newValue)}
          validation={showError && validateLocationName(locationName)}
          disabled={disabled}
        />

        {/* /!*Address*!/ */}
        <div className={classes.addressSection}>
          {/* UNIT */}
          <FormTextField
            label='Unit'
            hint='Max. 15 characters'
            value={unit}
            onChange={(newValue) => updateAddressData('unit', newValue)}
            validation={showError && validateUnitNumber(unit)}
            disabled={disabled}
          />
          {/* ADDRESS LINE 1 */}
          <FormTextField
            label='Address Line 1'
            hint='Max. 55 characters'
            required
            value={addressLine1}
            onChange={(newValue) => updateAddressData('addressLine1', newValue)}
            disabled={buildingNameWasSelected || disabled}
            validation={showError && validateAddress(addressLine1)}
          />
          {/* ADDRESS LINE 2 */}
          <FormTextField
            label='Address Line 2'
            hint='Max. 55 characters'
            value={addressLine2}
            onChange={(newValue) => updateAddressData('addressLine2', newValue)}
            disabled={buildingNameWasSelected || disabled}
            validation={showError && validateAddress2(addressLine2)}
          />

          <div className={classes.cityProvinceDiv}>
            {/* CITY */}
            <FormTextField
              label='City'
              hint='Max. 45 characters'
              required
              value={city}
              onChange={(newValue) => updateAddressData('city', newValue)}
              disabled={buildingNameWasSelected || disabled}
              validation={showError && validateCity(city)}
            />
            {/* PROVINCE */}
            <ProvinceSelect
              value={province}
              onChange={(newValue) => updateAddressData('province', newValue)}
              disabled={buildingNameWasSelected || disabled}
              validation={showError && validateProvince(province)}
            />
          </div>

          {/* POSTAL CODE */}
          <FormTextField
            label='Postal Code'
            required
            value={postalCode}
            onChange={(newValue) => updateAddressData('postalCode', newValue)}
            disabled={buildingNameWasSelected || disabled}
            validation={showError && fieldType !== 'Autocomplete' && validatePostalCode(postalCode)}
          />
          {/* COUNTRY */}
          <CountrySelect
            value={country}
            onChange={(newValue) => updateAddressData('country', newValue)}
            disabled={buildingNameWasSelected || disabled}
          />
          {/* Phone Number */}
          <PhoneField
            label='Location Phone'
            value={phoneNumber}
            onChange={(newValue) => updateAddressData('phoneNumber', newValue)}
            disabled={buildingNameWasSelected || disabled}
            validation={showError && fieldType !== 'Autocomplete' && validatePhoneNumber(phoneNumber)}
          />

        </div>

        {/* SQUARE FOOTAGE */}
        <FormTextField
          label='Square Footage'
          required
          hint='Max. 25 digits'
          value={sqFootage}
          onChange={(newValue) => updateLocationData('sqFootage', newValue)}
          validation={showError && validateSqFootage(sqFootage)}
          disabled={disabled}
        />
        {/* FMZ */}
        <FormTextField
          label={words.fmz}
          hint='Max. 29 characters'
          value={fmz}
          onChange={(newValue) => updateLocationData('fmz', newValue)}
          validation={showError && validateFMZ(fmz)}
          disabled={disabled}
        />
        {/* SECURITY ACCESS */}
        <FormTextField
          label='Security Access'
          hint='Max. 255 characters'
          value={securityAccess}
          onChange={(newValue) => updateLocationData('securityAccess', newValue)}
          disabled={disabled}
          validation={showError && validateSecurityAccess(securityAccess)}
          rows={3}
        />
        {/* LAST INSPECTION DATE */}
        <LastInspectionDatePicker
          disabled={disabled}
          value={lastInspectionDate}
          onChange={(newValue) => updateLocationData('lastInspectionDate', newValue)}
        />
        {/* DOCUMENTS */}
        <DocumentsImporter
          className={classes.documentsImporter}
          disabled={disabled}
          importDocuments={documentList}
          setImportDocuments={(newValue) => updateLocationData('documentList', newValue)}
          showError={showError}
        />
      </div>
      <ButtonLayoutDiv>
        {onCancel && <CancelButton onClick={onCancel} />}
        {goToPreviousPage && <PrevButton onClick={prevHandler} />}
        {goToNextPage && <NextButton onClick={nextHandler} />}
      </ButtonLayoutDiv>
      {addressErrorSnackbar && (
        <FeedbackSnackbar snackbar={addressErrorSnackbar} setSnackbar={setAddressErrorSnackbar} />
      )}
      <FormErrorSnackbar isSnackbarOpen={isSnackbarOpen} setIsSnackbarOpen={setIsSnackbarOpen} />
    </div>
  )
}

export default LocationImportTemplate

const useStyles = makeStyles({
  root: {
    paddingBottom: '17px'
  },
  locationForm: {
    display: 'flex',
    flexDirection: 'column',
    margin: '20px 0',
    maxWidth: '600px'
  },
  addressSection: {
    display: 'flex',
    flexDirection: 'column',
    margin: '40px 0 60px'
  },
  textField: {
    width: '100%',
    '& .MuiInputBase-root': {
      width: '100%'
    },
    '& .MuiInputBase-input': {
      padding: 0
    }
  },
  cityProvinceDiv: {
    display: 'flex',

    '&>div': {
      width: '50%',

      '&:first-of-type': {
        marginRight: '5px'
      },
      '&:nth-of-type(2)': {
        marginLeft: '5px'
      }
    }
  },
  documentsImporter: {
    marginTop: '20px'
  }
})
